import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faComments, faMagnifyingGlass, faTriangleExclamation, faEnvelopeOpenText, faPenToSquare
} from '@fortawesome/free-solid-svg-icons';

import VoiceOriginContext from '../../context/VoiceOriginContext';
import Avatar from '../Avatar';
import GroupIcon from '../chat/GroupIcon';
import { stamp } from '../chat/ConversationList';
import GuestChat from './GuestChat';
import GuestNewChat from './GuestNewChat';

/**
 * The guest's chats, laid out like the staff messenger (pages/Chats.js): their
 * conversations on the left, the open one on the right, one pane at a time on
 * a phone. It uses the same msgr-* styles so the two read as one product.
 *
 * The list is its own rather than the staff ConversationList, which carries
 * staff-only parts - live presence, links to employee profiles - that have no
 * place in front of somebody from outside the company.
 */

const KIND_LABEL = { image: 'Photo', audio: 'Voice note', video: 'Video', document: 'Document' };

const preview = (chat) => {
    const last = chat.lastMessage;
    if (!last) return chat.kind === 'app' ? 'Notifications' : chat.role === 'viewer' ? 'View only' : 'No messages yet';
    const text = last.text || KIND_LABEL[last.kind] || '';
    if (last.mine) return `You: ${text}`;
    // In a one-to-one chat the name is already the title.
    if (chat.kind === 'direct' || !last.senderName) return text;
    return `${last.senderName.split(' ')[0]}: ${text}`;
};

const ChatAvatar = ({ chat }) => (chat.kind === 'direct'
    ? <Avatar name={chat.name} profilePic={chat.avatar} className="msgr-avatar" />
    : <GroupIcon conversation={{ kind: chat.kind, name: chat.name, avatar: chat.avatar }} className="msgr-avatar" />);

const GuestMessenger = ({ chats, onActivity }) => {
    const { conversationId } = useParams();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [gone, setGone] = useState('');
    // The "New message" people list, shown in place of the chat list.
    const [picking, setPicking] = useState(false);
    useEffect(() => { setGone(''); }, [conversationId]);

    const joined = useMemo(() => (chats || []).filter((c) => c.status === 'active'), [chats]);
    const waiting = useMemo(() => (chats || []).filter((c) => c.status !== 'active'), [chats]);
    const term = query.trim().toLowerCase();
    const shown = term ? joined.filter((c) => c.name.toLowerCase().includes(term)) : joined;
    const open = joined.find((c) => String(c.conversationId) === String(conversationId));

    return (
        <div className={`msgr guest-msgr ${conversationId ? 'has-open' : ''}`}>
            <div className="msgr-sidebar">
                {picking ? <GuestNewChat onClose={() => setPicking(false)} onOpened={onActivity} /> : <>
                <div className="msgr-sidebar-head">
                    <div className="msgr-sidebar-title">
                        <h3>Chats</h3>
                        <div className="msgr-head-actions">
                            <button type="button" className="msgr-icon-btn" title="New message" aria-label="New message" onClick={() => setPicking(true)}>
                                <FontAwesomeIcon icon={faPenToSquare} />
                            </button>
                        </div>
                    </div>
                    <div className="msgr-search">
                        <FontAwesomeIcon icon={faMagnifyingGlass} className="msgr-search-icon" />
                        <input value={query} placeholder="Search your chats" onChange={(e) => setQuery(e.target.value)} />
                    </div>
                </div>

                <div className="msgr-list">
                    {waiting.map((c) => (
                        <div key={c.conversationId} className="msgr-row guest-invite-row"
                            onClick={() => navigate(`/portal/${c.inviteToken}`)}>
                            <ChatAvatar chat={c} />
                            <div className="msgr-row-body">
                                <div className="msgr-row-top"><span className="msgr-row-name">{c.name}</span></div>
                                <div className="msgr-row-bottom">
                                    <span className="msgr-row-preview">
                                        <FontAwesomeIcon icon={faEnvelopeOpenText} />{' '}
                                        {c.status === 'pending' ? 'Waiting for approval' : 'Invitation - open to accept'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}

                    {chats === null && <div className="msgr-empty">Loading…</div>}
                    {chats && !joined.length && !waiting.length && (
                        <div className="msgr-empty">No conversations yet.<br />When someone at GTS adds you to a group, it will appear here.</div>
                    )}
                    {chats && joined.length > 0 && !shown.length && <div className="msgr-empty">Nothing matches that.</div>}

                    {shown.map((c) => (
                        <div key={c.conversationId}
                            className={`msgr-row ${String(c.conversationId) === String(conversationId) ? 'active' : ''} ${c.unread ? 'unread' : ''}`}
                            onClick={() => navigate(`/guest/chats/${c.conversationId}`)}>
                            <ChatAvatar chat={c} />
                            <div className="msgr-row-body">
                                <div className="msgr-row-top">
                                    <span className="msgr-row-name">{c.name}</span>
                                    <span className="msgr-row-time">{stamp(c.lastMessage?.at || c.lastActivityAt)}</span>
                                </div>
                                <div className="msgr-row-bottom">
                                    <span className="msgr-row-preview">{preview(c)}</span>
                                    {c.unread > 0 && <span className="msgr-badge">{c.unread > 99 ? '99+' : c.unread}</span>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                </>}
            </div>

            <div className="msgr-main guest-msgr-main">
                {!conversationId ? (
                    <div className="msgr-placeholder guest-placeholder">
                        <FontAwesomeIcon icon={faComments} />
                        <p>Choose a conversation to start chatting.</p>
                    </div>
                ) : gone ? (
                    <div className="msgr-placeholder guest-placeholder">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        <h3>This conversation is not available</h3>
                        <p>{gone}</p>
                    </div>
                ) : (
                    <VoiceOriginContext.Provider value={{ title: open?.name || 'Conversation', href: `/guest/chats/${conversationId}` }}>
                        <GuestChat
                            embedded
                            conversationId={conversationId}
                            onBack={() => navigate('/guest/chats')}
                            onGone={(message) => { setGone(message); onActivity(); }}
                            onActivity={onActivity}
                        />
                    </VoiceOriginContext.Provider>
                )}
            </div>
        </div>
    );
};

export default GuestMessenger;
